import { useState, type ReactNode } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { listingRequirementsData, listingRequirementsDesign, listingRequirementDifferences } from "./listing-requirements";
import { useMe } from "@/auth/use-me";
import { Tooltip } from "@/components/common/Tooltip";
import { ListingRequirementsDiff } from "./ListingRequirementsDiff";
import { Button } from "@/components/design/Button";
import { matchToData, type GenesisMatch } from "@/components/match/match-design-mapping";
import type { MatchData } from "@/components/match/match-design-types";
import { AccessLevel, ListingMatchRequirementsDocument, CreateListingMatchVersionDocument, MatchDetailDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

export function ListingMatchSetup({ listingId, matchId, canManage, historical, pending, onStarted, onOpenRequirements, versionControl, beforePrepare }: {
  onOpenRequirements?: () => void;
  beforePrepare?: () => Promise<void>;
  versionControl?: ReactNode;
  listingId: string; matchId: string; canManage: boolean; historical: boolean; pending: boolean; onStarted: () => Promise<unknown>;
}) {
  const intl = useIntl();
  const query = useQuery(MatchDetailDocument, { variables: { id: matchId }, fetchPolicy: "cache-and-network" });
  const source = useQuery(ListingMatchRequirementsDocument, { variables: { id: listingId }, fetchPolicy: "cache-and-network" });
  const shown = query.data?.match;
  const differences = shown && source.data?.listing ? listingRequirementDifferences(matchToData(shown),
    source.data.listing.data as Record<string, unknown> ?? {}) : [];
  const changed = differences.length > 0;
  const requirementsLink = <Link id="listing-match-requirements-tab" to={`/share/listings/${listingId}?tab=team`}
          onClick={(event) => {
            if (onOpenRequirements && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
              event.preventDefault();
              onOpenRequirements();
            }
          }}
          className="smycles-field-meta text-ink underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30">
          <FormattedMessage id={changed ? "share.listings.versions.changed" : "share.listings.versions.requirementsLink"} />
        </Link>;
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
        {versionControl}
        {changed ? <Tooltip interactive placement="bottom" contentClassName="max-w-sm max-h-[80vh]"
          content={<ListingRequirementsDiff differences={differences} />}>
          {requirementsLink}
        </Tooltip> : requirementsLink}
      </div>
      {query.error ? <p role="alert">{userErrorMessage(intl, query.error)}</p> : shown ? (
        <Setup key={matchId} match={shown} listingId={listingId} canManage={canManage}
          historical={historical} pending={pending} onStarted={onStarted} beforePrepare={beforePrepare} />
      ) : <p aria-busy="true"><FormattedMessage id="common.loading" /></p>}
    </div>
  );
}

function Setup({ match, listingId, canManage, historical, pending, onStarted, beforePrepare }: {
  beforePrepare?: () => Promise<void>;
  match: GenesisMatch; listingId: string; canManage: boolean; historical: boolean; pending: boolean; onStarted: () => Promise<unknown>;
}) {
  const intl = useIntl();
  const me = useMe();
  const apollo = useApolloClient();
  const [preparing, setPreparing] = useState(false);
  const [draft, setDraft] = useState<MatchData | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [create, { loading }] = useMutation(CreateListingMatchVersionDocument);
  const canDesign = match.createdBy?.id === me.id || match.access?.some((a) => a.person.id === me.id && a.design === AccessLevel.Write);
  const canRevise = canManage && canDesign && match.actions.canSetAutoMatch && !historical && !pending;
  const data = draft ?? matchToData(match);
  const busy = loading || preparing;
  async function prepare() {
    setPreparing(true);
    setError(null);
    try {
      await beforePrepare?.();
      const source = await apollo.query({ query: ListingMatchRequirementsDocument,
        variables: { id: listingId }, fetchPolicy: "network-only" });
      if (!source.data?.listing) throw new Error("Missing listing requirements");
      const sourceData = source.data.listing.data as Record<string, unknown> | null;
      setDraft({ ...matchToData(match), ...listingRequirementsData(sourceData ?? {}) });
      setRequestId(null);
    } catch (e) { setError(userErrorMessage(intl, e)); }
    finally { setPreparing(false); }
  }
  async function start() {
    setError(null);
    const id = requestId ?? crypto.randomUUID();
    setRequestId(id);
    try {
      await create({ variables: { input: { listingId, sourceMatchId: match.id, requestId: id,
        design: { ...match.design, ...listingRequirementsDesign(data) } } } });
      setDraft(null);
      await onStarted();
    } catch (e) { setError(userErrorMessage(intl, e)); }
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      {draft ? <p className="smycles-field-meta text-ink-muted"><FormattedMessage id="share.listings.versions.requirementsSnapshot" /></p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {canRevise ? <div className="flex flex-wrap gap-2">
        {draft ? <>
          <Button onClick={() => void start()} disabled={busy} aria-busy={busy}>
            <FormattedMessage id="share.listings.versions.rankAll" />
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => { setDraft(null); setError(null); }}>
            <FormattedMessage id="common.cancel" />
          </Button>
        </> : <Button variant="secondary" disabled={busy} aria-busy={busy} onClick={() => void prepare()}>
          <FormattedMessage id="share.listings.versions.edit" />
        </Button>}
      </div> : null}
    </div>
  );
}
